"use client";

/**
 * AI DC north-south bandwidth calculator.
 *
 * State ownership:
 *   - The six inputs live in component state.
 *   - Every input change writes back to the URL query string via
 *     router.replace() so the URL is the shareable representation.
 *   - No localStorage / sessionStorage / cookie — this tool is
 *     deliberately outside the consent framework.
 *
 * Rendering:
 *   - Recompute is synchronous on every input change. The model is a
 *     handful of multiplies; no debounce.
 *   - Every displayed number goes through the formatters in
 *     bandwidth-model.ts — no raw float reaches the DOM.
 *   - No framer-motion transitions here: prefers-reduced-motion is the
 *     default state, not an override.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "@/components/LocalizedLink";
import { useRouter, usePathname } from "next/navigation";
import { useTranslation } from "@/lib/i18n";
import {
  ARCHETYPES,
  Archetype,
  BandwidthInputs,
  DEFAULT_INPUTS,
  FLOW_IDS,
  FlowId,
  INPUT_BOUNDS,
  computeBandwidth,
  formatGbps,
  formatTbps,
  inputsToSearchParams,
  parseSearchParams,
  round,
} from "@/lib/bandwidth-model";

/* ─────────────────────────────────────────────────────────────────────────────
   Local constants
───────────────────────────────────────────────────────────────────────────── */

// Flow order controls the chart-row order and reads left-to-right the same
// way the model reasons about them: checkpoint is the peak driver at the
// top, ops is background chatter at the bottom.
const FLOW_ORDER: readonly FlowId[] = [
  "checkpoint",
  "dataset",
  "inference",
  "distill",
  "ops",
];

// Mapped to CSS custom properties defined in globals.css (see the
// "Series palette" block). Colour alone must not carry meaning; every
// row also has a text label and an aria-label.
const FLOW_COLOR: Record<FlowId, string> = {
  checkpoint: "var(--series-1)",
  dataset: "var(--series-2)",
  inference: "var(--series-3)",
  distill: "var(--series-4)",
  ops: "var(--series-5)",
};

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */

export function BandwidthCalculatorClient() {
  const { t, language } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  // Start from the defaults so the calculator is part of the prerendered HTML,
  // then apply ?query values on mount. useSearchParams() would make the whole
  // calculator client-only on this statically rendered page.
  const [inputs, setInputs] = useState<BandwidthInputs>(() =>
    parseSearchParams(new URLSearchParams()),
  );
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    const query = window.location.search;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydrate from the URL
    if (query) setInputs(parseSearchParams(new URLSearchParams(query)));
    setRestored(true);
  }, []);
  const [copyState, setCopyState] = useState<"idle" | "summary" | "link">("idle");
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Push URL query string on every input change. router.replace keeps the
  // history stack clean — the calculator is one page, not N snapshots.
  useEffect(() => {
    if (!restored) return; // don't overwrite incoming ?query before it is read
    const next = new URLSearchParams(inputsToSearchParams(inputs)).toString();
    const current = window.location.search.replace(/^\?/, "");
    if (next !== current) {
      router.replace(`${pathname}?${next}`, { scroll: false });
    }
  }, [inputs, pathname, router, restored]);

  const result = useMemo(() => computeBandwidth(inputs), [inputs]);

  const set = useCallback(
    <K extends keyof BandwidthInputs>(key: K, value: BandwidthInputs[K]) => {
      setInputs((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const reset = useCallback(() => setInputs(DEFAULT_INPUTS), []);

  const flashCopyState = (state: "summary" | "link") => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
    setCopyState(state);
    copyTimer.current = setTimeout(() => setCopyState("idle"), 1800);
  };

  const copySummary = useCallback(async () => {
    const text = buildPlainSummary(result, t);
    try {
      await navigator.clipboard.writeText(text);
      flashCopyState("summary");
    } catch {
      // no-op — clipboard blocked; button label stays idle.
    }
  }, [result, t]);

  const copyShareableLink = useCallback(async () => {
    const url = `${window.location.origin}${pathname}?${new URLSearchParams(
      inputsToSearchParams(inputs),
    ).toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      flashCopyState("link");
    } catch {
      // no-op
    }
  }, [inputs, pathname]);

  // Bilingual href for the configurator CTA — no pre-fill by design.
  const configuratorHref = language === "bg" ? "/bg/#configurator" : "/#configurator";
  const contactHref = language === "bg" ? "/bg/contact" : "/contact";

  return (
    <main className="min-h-screen bg-background text-foreground">
      <section className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
        {/* ─── Header ─── */}
        <p className="text-xs font-mono uppercase tracking-widest text-eyebrow">
          {t("bw_eyebrow")}
        </p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-semibold text-foreground">
          {t("bw_h1")}
        </h1>
        <p className="mt-4 max-w-3xl text-base text-muted-foreground">
          {t("bw_intro")}
        </p>

        {/* Indicative line — same register as the configurator's disclaimer. */}
        <p
          role="note"
          className="mt-4 inline-flex items-center gap-2 rounded-lg border border-card-border bg-surface-light px-3 py-2 text-xs font-mono text-muted-foreground"
        >
          <span
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{ background: "var(--eyebrow)" }}
          />
          {t("bw_indicative_line")}
        </p>

        {/* ─── Primer: three field-honest observations before the sliders ─── */}
        <Primer />

        {/* ─── Body: inputs + results ─── */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          {/* Inputs column */}
          <div className="lg:col-span-2">
            <InputsPanel inputs={inputs} phi={result.phi} set={set} reset={reset} />
          </div>

          {/* Results column */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <MetricCards
              avgTbps={result.totals.avgTbps}
              peakTbps={result.totals.peakTbps}
              linkTbps={result.totals.linkTbps}
              avgPerMw={result.perMw.avgGbps}
              peakPerMw={result.perMw.peakGbps}
              l400={result.lambdas.l400}
            />

            <FlowBreakdownChart result={result} />

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={copySummary}
                className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-surface-light transition-colors motion-reduce:transition-none"
              >
                {copyState === "summary" ? t("bw_copy_done") : t("bw_copy_button")}
              </button>
              <button
                type="button"
                onClick={copyShareableLink}
                className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-surface-light transition-colors motion-reduce:transition-none"
              >
                {copyState === "link" ? t("bw_share_copied") : t("bw_share_button")}
              </button>
            </div>
          </div>
        </div>

        {/* ─── Methodology notes ─── */}
        <div className="mt-12 rounded-2xl border border-card-border bg-card-bg p-6">
          <h2 className="text-lg font-semibold text-foreground">
            {t("bw_notes_heading")}
          </h2>
          <div className="mt-4 divide-y divide-divider">
            <MethodNote
              title={t("bw_note_mw_title")}
              body={t("bw_note_mw_body")}
            />
            <MethodNote
              title={t("bw_note_phi_title")}
              body={t("bw_note_phi_body")}
            />
            <MethodNote
              title={t("bw_note_limits_title")}
              body={t("bw_note_limits_body")}
            />
          </div>
        </div>

        {/* ─── CTA ─── */}
        <div className="mt-10 rounded-2xl border border-card-border bg-surface-light p-6">
          <h2 className="text-xl font-semibold text-foreground">
            {t("bw_cta_heading")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {t("bw_cta_body")}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              href={contactHref}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium btn-primary transition-colors motion-reduce:transition-none"
            >
              {t("bw_cta_primary")}
            </Link>
            <Link
              href={configuratorHref}
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium border border-border text-foreground hover:bg-surface-light transition-colors motion-reduce:transition-none"
            >
              {t("bw_cta_secondary")}
            </Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{t("bw_cta_note")}</p>
        </div>
      </section>
    </main>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Primer — three field observations rendered as short cards.
   Purely presentational: no inputs, no state, no dynamic numbers. The
   grid collapses to a single column below 640px so each card keeps its
   full body width on a phone.
───────────────────────────────────────────────────────────────────────────── */

function Primer() {
  const { t } = useTranslation();
  const items: Array<{ title: string; body: string }> = [
    { title: t("bw_primer_1_title"), body: t("bw_primer_1_body") },
    { title: t("bw_primer_2_title"), body: t("bw_primer_2_body") },
    { title: t("bw_primer_3_title"), body: t("bw_primer_3_body") },
  ];
  return (
    <section aria-labelledby="bw-primer-heading" className="mt-8">
      <h2
        id="bw-primer-heading"
        className="text-xs font-mono uppercase tracking-widest text-eyebrow"
      >
        {t("bw_primer_heading")}
      </h2>
      <ul className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {items.map((item, i) => (
          <li
            key={i}
            className="rounded-2xl border border-card-border bg-card-bg p-5"
          >
            <p className="text-sm font-semibold text-foreground">
              {item.title}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {item.body}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Inputs panel
───────────────────────────────────────────────────────────────────────────── */

function InputsPanel({
  inputs,
  phi,
  set,
  reset,
}: {
  inputs: BandwidthInputs;
  phi: number;
  set: <K extends keyof BandwidthInputs>(key: K, value: BandwidthInputs[K]) => void;
  reset: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-2xl border border-card-border bg-card-bg p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {t("bw_step_inputs")}
          </span>
        </div>
        <button
          type="button"
          onClick={reset}
          className="text-xs font-mono text-muted-foreground hover:text-foreground"
        >
          {t("bw_reset_button")}
        </button>
      </div>

      <ArchRadioGroup value={inputs.arch} onChange={(v) => set("arch", v)} />

      <SliderRow
        id="bw-mw"
        label={t("bw_mw_label")}
        hint={t("bw_mw_hint")}
        value={inputs.mw}
        min={INPUT_BOUNDS.mw.min}
        max={INPUT_BOUNDS.mw.max}
        step={INPUT_BOUNDS.mw.step}
        unit={t("bw_mw_unit")}
        format={(v) => `${v}`}
        onChange={(v) => set("mw", v)}
      />
      <SliderRow
        id="bw-P"
        label={t("bw_P_label")}
        hint={t("bw_P_hint")}
        value={inputs.P}
        min={INPUT_BOUNDS.P.min}
        max={INPUT_BOUNDS.P.max}
        step={INPUT_BOUNDS.P.step}
        unit={t("bw_P_unit")}
        format={(v) => v.toFixed(1)}
        onChange={(v) => set("P", round(v, 1))}
      />
      <SliderRow
        id="bw-drain"
        label={t("bw_drain_label")}
        hint={t("bw_drain_hint")}
        value={inputs.drain}
        min={INPUT_BOUNDS.drain.min}
        max={INPUT_BOUNDS.drain.max}
        step={INPUT_BOUNDS.drain.step}
        unit={t("bw_drain_unit")}
        format={(v) => `${v}`}
        onChange={(v) => set("drain", v)}
      />
      <SliderRow
        id="bw-interval"
        label={t("bw_interval_label")}
        hint={t("bw_interval_hint")}
        value={inputs.interval}
        min={INPUT_BOUNDS.interval.min}
        max={INPUT_BOUNDS.interval.max}
        step={INPUT_BOUNDS.interval.step}
        unit={t("bw_interval_unit")}
        format={(v) => `${v}`}
        onChange={(v) => set("interval", v)}
      />
      <SliderRow
        id="bw-bursters"
        label={t("bw_bursters_label")}
        hint={t("bw_bursters_hint")}
        value={inputs.bursters}
        min={INPUT_BOUNDS.bursters.min}
        max={INPUT_BOUNDS.bursters.max}
        step={INPUT_BOUNDS.bursters.step}
        unit=""
        // Bursters row shows Φ in the readout as required by the brief.
        formatReadout={(v) =>
          t("bw_bursters_readout")
            .replace("{count}", String(v))
            .replace("{phi}", phi.toFixed(2))
        }
        format={(v) => `${v}`}
        onChange={(v) => set("bursters", v)}
      />
    </div>
  );
}

/* Archetype radio group ─────────────────────────────────────────────────── */

function ArchRadioGroup({
  value,
  onChange,
}: {
  value: Archetype;
  onChange: (v: Archetype) => void;
}) {
  const { t } = useTranslation();
  const labels: Record<Archetype, string> = {
    cap: t("bw_arch_cap"),
    mix: t("bw_arch_mix"),
    inf: t("bw_arch_inf"),
  };
  const descs: Record<Archetype, string> = {
    cap: t("bw_arch_cap_desc"),
    mix: t("bw_arch_mix_desc"),
    inf: t("bw_arch_inf_desc"),
  };

  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Arrow-key navigation as required by role="radiogroup".
    const idx = ARCHETYPES.indexOf(value);
    if (idx === -1) return;
    let next = idx;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (idx + 1) % ARCHETYPES.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp")
      next = (idx - 1 + ARCHETYPES.length) % ARCHETYPES.length;
    else return;
    e.preventDefault();
    onChange(ARCHETYPES[next]);
  };

  return (
    <fieldset className="mb-5">
      <legend className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("bw_arch_label")}
      </legend>
      <div
        role="radiogroup"
        aria-label={t("bw_arch_label")}
        onKeyDown={onKey}
        className="grid grid-cols-3 gap-1.5 rounded-xl border border-border p-1"
      >
        {ARCHETYPES.map((a) => {
          const selected = value === a;
          return (
            <button
              key={a}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(a)}
              className={
                selected
                  ? "rounded-lg px-2 py-2 text-xs sm:text-sm font-semibold bg-accent text-on-accent transition-colors motion-reduce:transition-none"
                  : "rounded-lg px-2 py-2 text-xs sm:text-sm font-medium text-foreground hover:bg-surface-light transition-colors motion-reduce:transition-none"
              }
            >
              {labels[a]}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{descs[value]}</p>
    </fieldset>
  );
}

/* Slider row ───────────────────────────────────────────────────────────── */

function SliderRow({
  id,
  label,
  hint,
  value,
  min,
  max,
  step,
  unit,
  format,
  formatReadout,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  format: (v: number) => string;
  /** Optional override for the on-screen readout (used by bursters). */
  formatReadout?: (v: number) => string;
  onChange: (v: number) => void;
}) {
  const readout = formatReadout ? formatReadout(value) : `${format(value)} ${unit}`.trim();
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </label>
        {/* Fixed-width readout: prevents layout shift as the number changes.
            tabular-nums keeps mono digits from re-flowing. */}
        <span className="min-w-[6.5rem] text-right font-mono text-sm text-foreground tabular-nums">
          {readout}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={readout}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-[color:var(--accent)] cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent)]"
      />
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Result cards
───────────────────────────────────────────────────────────────────────────── */

function MetricCards({
  avgTbps,
  peakTbps,
  linkTbps,
  avgPerMw,
  peakPerMw,
  l400,
}: {
  avgTbps: number;
  peakTbps: number;
  linkTbps: number;
  avgPerMw: number;
  peakPerMw: number;
  l400: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <MetricCard
        title={t("bw_card_avg_title")}
        value={`${formatTbps(avgTbps, 2)} Tb/s`}
        sub={t("bw_card_avg_sub").replace("{value}", formatGbps(avgPerMw).replace(" G", ""))}
      />
      <MetricCard
        title={t("bw_card_peak_title")}
        value={`${formatTbps(peakTbps, 1)} Tb/s`}
        sub={t("bw_card_peak_sub").replace("{value}", formatGbps(peakPerMw).replace(" G", ""))}
      />
      <MetricCard
        title={t("bw_card_link_title")}
        value={`${formatTbps(linkTbps, 1)} Tb/s`}
        sub={t("bw_card_link_sub")}
      />
      <MetricCard
        title={t("bw_card_lambdas_title")}
        value={t("bw_card_lambdas_value").replace("{n}", String(l400))}
        sub={t("bw_card_lambdas_sub")}
      />
    </div>
  );
}

function MetricCard({
  title,
  value,
  sub,
}: {
  title: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-2xl border border-card-border bg-card-bg p-5">
      <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
        {title}
      </p>
      {/* Fixed line-height + reserved min-height so the number swap does not
          shift the card below it. */}
      <p className="mt-2 min-h-[2.5rem] font-mono text-3xl font-semibold text-foreground tabular-nums leading-tight">
        {value}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Flow breakdown chart
───────────────────────────────────────────────────────────────────────────── */

function FlowBreakdownChart({
  result,
}: {
  result: ReturnType<typeof computeBandwidth>;
}) {
  const { t } = useTranslation();
  const flowLabels: Record<FlowId, string> = {
    checkpoint: t("bw_flow_checkpoint"),
    dataset: t("bw_flow_dataset"),
    inference: t("bw_flow_inference"),
    distill: t("bw_flow_distill"),
    ops: t("bw_flow_ops"),
  };

  const peakTotal = FLOW_IDS.reduce((s, id) => s + result.flows[id].peak, 0);

  return (
    <div className="rounded-2xl border border-card-border bg-card-bg p-5">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-foreground">
          {t("bw_breakdown_title")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("bw_breakdown_sub")}
        </p>
      </div>

      {/* Visually hidden list — screen readers get the numbers directly
          without decoding the bars. Colour alone must not carry meaning. */}
      <ul className="sr-only">
        {FLOW_ORDER.map((id) => {
          const share = peakTotal > 0 ? (result.flows[id].peak / peakTotal) * 100 : 0;
          return (
            <li key={id}>
              {t("bw_flow_row_aria")
                .replace("{flow}", flowLabels[id])
                .replace("{value}", formatGbps(result.flows[id].peak))
                .replace("{share}", String(Math.round(share)))}
            </li>
          );
        })}
      </ul>

      <div className="space-y-2" aria-hidden="true">
        {FLOW_ORDER.map((id) => {
          const peak = result.flows[id].peak;
          const share = peakTotal > 0 ? peak / peakTotal : 0;
          return (
            <div key={id} className="grid grid-cols-[10rem_1fr_5rem] items-center gap-3">
              <span className="truncate text-xs font-medium text-foreground">
                {flowLabels[id]}
              </span>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-light">
                <div
                  className="h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none"
                  style={{
                    width: `${round(share * 100, 1)}%`,
                    background: FLOW_COLOR[id],
                  }}
                />
              </div>
              <span className="text-right font-mono text-xs text-foreground tabular-nums">
                {formatGbps(peak)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Methodology notes — collapsible <details>. Collapsed by default.
───────────────────────────────────────────────────────────────────────────── */

function MethodNote({ title, body }: { title: string; body: string }) {
  return (
    <details className="group py-3">
      <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-medium text-foreground marker:content-['']">
        <span>{title}</span>
        <span
          aria-hidden="true"
          className="text-xs font-mono text-muted-foreground transition-transform duration-200 motion-reduce:transition-none group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <p className="mt-3 pr-2 text-sm leading-relaxed text-muted-foreground">
        {body}
      </p>
    </details>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Plain-text summary for the copy button.
   Includes the methodology line so a figure pasted into an email does not
   travel without its caveat.
───────────────────────────────────────────────────────────────────────────── */

type TFunction = ReturnType<typeof useTranslation>["t"];

function buildPlainSummary(
  result: ReturnType<typeof computeBandwidth>,
  t: TFunction,
): string {
  const i = result.inputs;
  const archLabel: Record<Archetype, string> = {
    cap: t("bw_arch_cap"),
    mix: t("bw_arch_mix"),
    inf: t("bw_arch_inf"),
  };
  const lines = [
    t("bw_copy_summary_title"),
    "",
    `${t("bw_arch_label")}: ${archLabel[i.arch]}`,
    `${t("bw_mw_label")}: ${i.mw} ${t("bw_mw_unit")}`,
    `${t("bw_P_label")}: ${i.P.toFixed(1)} ${t("bw_P_unit")}`,
    `${t("bw_drain_label")}: ${i.drain} ${t("bw_drain_unit")}`,
    `${t("bw_interval_label")}: ${i.interval} ${t("bw_interval_unit")}`,
    `${t("bw_bursters_label")}: ${i.bursters} (\u03a6 ${result.phi.toFixed(2)})`,
    "",
    `${t("bw_card_avg_title")}: ${formatTbps(result.totals.avgTbps, 2)} Tb/s`,
    `${t("bw_card_peak_title")}: ${formatTbps(result.totals.peakTbps, 1)} Tb/s`,
    `${t("bw_card_link_title")}: ${formatTbps(result.totals.linkTbps, 1)} Tb/s`,
    `${t("bw_card_lambdas_title")}: ${result.lambdas.l400} \u00d7400G`,
    "",
    t("bw_copy_summary_methodology"),
  ];
  return lines.join("\n");
}
